import * as React from 'react'
import { AlertCircle, Eye, Loader2, Send } from 'lucide-react'
import { create } from '@bufbuild/protobuf'
import { useMutation } from '@connectrpc/connect-query'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { EmailBodyFrame } from '@/components/emails/email-body-frame'
import { RecipientChipsInput } from '@/components/emails/recipient-chips-input'
import { SystemTemplateSlotEditor } from '@/components/settings/email/system-template-slot-editor'
import {
  TemplateVariablePicker,
  buildSystemTemplateVariableOptions,
  type TemplateVariableOption,
} from '@/components/settings/email/template-variable-picker'
import {
  TemplateVariableTextEditor,
  type TemplateVariableTextEditorHandle,
} from '@/components/settings/email/template-variable-text-editor'
import type { RichTextVariableToken } from '@/hooks/use-rich-text-editor'
import { extractTemplateVariables } from '@/lib/template-variable-tokens'
import { useSystemEmailTemplate } from '@/stores/system-email-templates.store'
import { SystemEmailTemplateType } from '@/gen/cortado/v1/emails_pb'
import { previewQuoteSendEmail } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  PreviewQuoteSendEmailRequestSchema,
  QuoteEmailOverrideSchema,
  type QuoteEmailOverride,
} from '@/gen/cortado/v1/cortado_pb'

export const MAX_ADDITIONAL_QUOTE_RECIPIENTS = 9

type InsertTarget = { type: 'subject' } | { type: 'slot'; key: string }

export interface QuoteSendDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  organizationId: string
  quoteId: string
  isDraft: boolean
  recipientName: string
  recipientEmail?: string
  isSending: boolean
  /** Blocks Send when the quote is payable but the org has no Stripe account. */
  stripeBlocksSend?: boolean
  onSend: (input: {
    additionalRecipientEmails: string[]
    emailOverride: QuoteEmailOverride
  }) => Promise<boolean | void> | boolean | void
}

/**
 * QuoteSendDialog composes the Quote Sent email for ONE send.
 *
 * It pre-fills from the org's saved "Quote Sent" system template and lets the
 * user edit the subject and all four slots for this send only — nothing here is
 * ever persisted back to the org template.
 *
 * Two details differ deliberately from the Settings template sheet:
 *  - It seeds EFFECTIVE values (`customValue || defaultValue`). The Settings
 *    sheet seeds '' meaning "inherit", which would show a composer an empty box
 *    for copy that is definitely going out.
 *  - Because presence is authoritative on the wire, clearing a slot here really
 *    clears it, rather than falling back to the org copy.
 */
export function QuoteSendDialog({
  open,
  onOpenChange,
  organizationId,
  quoteId,
  isDraft,
  recipientName,
  recipientEmail,
  isSending,
  stripeBlocksSend = false,
  onSend,
}: QuoteSendDialogProps) {
  const { template, isLoading: isTemplateLoading } = useSystemEmailTemplate(
    organizationId,
    SystemEmailTemplateType.QUOTE_SENT
  )
  const previewMutation = useMutation(previewQuoteSendEmail)

  const [activeTab, setActiveTab] = React.useState<'compose' | 'preview'>('compose')
  const [subject, setSubject] = React.useState('')
  const [slotValues, setSlotValues] = React.useState<Record<string, string>>({})
  const [additionalRecipients, setAdditionalRecipients] = React.useState<string[]>([])
  const [insertTarget, setInsertTarget] = React.useState<InsertTarget>({ type: 'subject' })
  const [seeded, setSeeded] = React.useState(false)

  const subjectEditorRef = React.useRef<TemplateVariableTextEditorHandle>(null)
  const slotRefs = React.useRef<Record<string, TemplateVariableTextEditorHandle | null>>({})

  const primaryEmail = recipientEmail?.trim() ?? ''
  const previewReset = previewMutation.reset

  // Seed once per opening, from the EFFECTIVE org copy.
  React.useEffect(() => {
    if (!open || seeded || !template) return
    setSubject(template.customSubject || template.defaultSubject)
    const seededSlots: Record<string, string> = {}
    for (const slot of template.textSlots) {
      seededSlots[slot.key] = slot.customValue || slot.defaultValue
    }
    setSlotValues(seededSlots)
    setInsertTarget(
      template.textSlots[0] ? { type: 'slot', key: template.textSlots[0].key } : { type: 'subject' }
    )
    setSeeded(true)
  }, [open, seeded, template])

  // Closing discards the whole draft: this override is for one send, and a
  // stale body leaking into the next quote would be worse than retyping.
  React.useEffect(() => {
    if (open) return
    setSeeded(false)
    setActiveTab('compose')
    setAdditionalRecipients([])
    setSubject('')
    setSlotValues({})
    previewReset()
  }, [open, previewReset])

  const variableOptions = React.useMemo(
    () => buildSystemTemplateVariableOptions(template?.availableVariables ?? []),
    [template?.availableVariables]
  )
  const variableTokens = React.useMemo<RichTextVariableToken[]>(
    () =>
      variableOptions.map((variable) => ({
        syntax: variable.syntax,
        label: variable.label,
        parentType: variable.parentType,
      })),
    [variableOptions]
  )

  const buildOverride = React.useCallback(
    () =>
      create(QuoteEmailOverrideSchema, {
        subject,
        textSlots: slotValues,
      }),
    [subject, slotValues]
  )

  // A token the backend's vocabulary doesn't know renders as a hard error at
  // send time (there is no silent fallback), so block before the send rather
  // than after. Known variables are stripped first; whatever survives is
  // genuinely unresolvable.
  const unresolvedVariables = React.useMemo(() => {
    const known = new Set(variableOptions.map((option) => option.syntax))
    const composed = [subject, ...Object.values(slotValues)].join(' ')
    return extractTemplateVariables(composed).filter((token) => !known.has(token))
  }, [subject, slotValues, variableOptions])

  const handleTabChange = (value: string) => {
    const tab = value as 'compose' | 'preview'
    setActiveTab(tab)
    if (tab === 'preview' && unresolvedVariables.length === 0) {
      previewMutation.mutate(
        create(PreviewQuoteSendEmailRequestSchema, {
          quoteId,
          emailOverride: buildOverride(),
        })
      )
    }
  }

  const handleInsertVariable = (variable: TemplateVariableOption) => {
    if (insertTarget.type === 'subject') {
      subjectEditorRef.current?.insertText(variable.syntax)
      return
    }
    slotRefs.current[insertTarget.key]?.insertText(variable.syntax)
  }

  const handleSend = async () => {
    const shouldClose = await onSend({
      additionalRecipientEmails: additionalRecipients,
      emailOverride: buildOverride(),
    })
    if (shouldClose !== false) onOpenChange(false)
  }

  const sendDisabled =
    isSending ||
    !primaryEmail ||
    stripeBlocksSend ||
    unresolvedVariables.length > 0 ||
    !subject.trim()

  const insertTargetLabel =
    insertTarget.type === 'subject'
      ? 'Subject Line'
      : (template?.textSlots.find((s) => s.key === insertTarget.key)?.label ?? 'selected field')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>{isDraft ? 'Publish and send quote' : 'Send portal link'}</DialogTitle>
          <DialogDescription>
            Edit this email for this send only. Your saved Quote Sent template is not changed.
          </DialogDescription>
        </DialogHeader>

        {isTemplateLoading || !template ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="compose">Compose</TabsTrigger>
              <TabsTrigger value="preview">
                <Eye className="mr-2 h-4 w-4" />
                Preview
              </TabsTrigger>
            </TabsList>

            <TabsContent
              value="compose"
              className="mt-6 max-h-[60dvh] space-y-6 overflow-y-auto pr-1"
            >
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs font-medium text-muted-foreground">
                    Primary recipient
                  </span>
                  <Badge variant="secondary">Contact</Badge>
                </div>
                {primaryEmail ? (
                  <div className="rounded-md border bg-muted/30 p-3">
                    <p className="truncate text-sm font-medium">{recipientName}</p>
                    <p className="truncate text-sm text-muted-foreground">{primaryEmail}</p>
                  </div>
                ) : (
                  <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                    This quote contact does not have an email address.
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="quote-send-additional-recipients">Additional recipients</Label>
                <RecipientChipsInput
                  id="quote-send-additional-recipients"
                  value={additionalRecipients}
                  onChange={setAdditionalRecipients}
                  disabled={isSending}
                  aria-label="Additional recipients"
                />
                <p className="text-xs text-muted-foreground">
                  Everyone receives this same email, each with their own portal link. Up to{' '}
                  {MAX_ADDITIONAL_QUOTE_RECIPIENTS} extra recipients.
                </p>
              </div>

              <Separator />

              <TemplateVariablePicker
                variables={variableOptions}
                title="Available Template Keys"
                helperText={`Click a key to insert it into ${insertTargetLabel}, or use the icon to copy it.`}
                onInsert={handleInsertVariable}
              />

              <div className="space-y-2">
                <Label htmlFor="quote-send-subject">Subject Line</Label>
                <TemplateVariableTextEditor
                  ref={subjectEditorRef}
                  id="quote-send-subject"
                  value={subject}
                  onChange={setSubject}
                  onFocus={() => setInsertTarget({ type: 'subject' })}
                  placeholder={template.defaultSubject}
                  variableTokens={variableTokens}
                  singleLine
                />
              </div>

              <div className="space-y-4">
                <h4 className="text-sm font-medium">Email Content</h4>
                {template.textSlots.map((slot) => (
                  <SystemTemplateSlotEditor
                    key={slot.key}
                    ref={(node) => {
                      slotRefs.current[slot.key] = node
                    }}
                    idPrefix="quote-send-slot"
                    slot={slot}
                    value={slotValues[slot.key] ?? ''}
                    onChange={(value) =>
                      setSlotValues((prev) => ({ ...prev, [slot.key]: value }))
                    }
                    onFocus={() => setInsertTarget({ type: 'slot', key: slot.key })}
                    variableTokens={variableTokens}
                  />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="preview" className="mt-6">
              {unresolvedVariables.length > 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  Resolve the template variables below to preview this email.
                </p>
              ) : previewMutation.isPending ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : previewMutation.data ? (
                <div className="overflow-hidden rounded-lg border">
                  <div className="space-y-1 border-b bg-muted/40 px-4 py-3">
                    <div className="flex items-baseline gap-2">
                      <span className="w-14 shrink-0 text-xs text-muted-foreground">To</span>
                      <span className="truncate text-sm">
                        {[primaryEmail, ...additionalRecipients].filter(Boolean).join(', ')}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="w-14 shrink-0 text-xs text-muted-foreground">Subject</span>
                      <span className="truncate text-sm font-medium">
                        {previewMutation.data.renderedSubject}
                      </span>
                    </div>
                  </div>
                  <div className="bg-muted/60 p-4">
                    <EmailBodyFrame
                      autoHeight={false}
                      className="mx-auto h-[52dvh] w-full max-w-[680px] rounded-md border shadow-sm"
                      html={previewMutation.data.renderedHtml}
                      title="Quote email preview"
                    />
                  </div>
                </div>
              ) : (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  Preview unavailable. Check the composed email for errors.
                </p>
              )}
            </TabsContent>
          </Tabs>
        )}

        {unresolvedVariables.length > 0 && (
          <Alert variant="destructive" className="py-2">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <span>Resolve these template variables before sending:</span>
              <span className="flex flex-wrap gap-1 pt-1">
                {unresolvedVariables.map((variable) => (
                  <Badge key={variable} variant="danger" className="font-mono">
                    {variable}
                  </Badge>
                ))}
              </span>
            </AlertDescription>
          </Alert>
        )}

        {stripeBlocksSend && (
          <Alert variant="destructive" className="py-2">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Connect Stripe before sending a quote the client has to pay.
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSending}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={sendDisabled}>
            {isSending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {isDraft ? 'Publish & Send' : 'Send Link'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
